import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { WebhookEchoPayloadViewer } from '@/components/admin/echo/WebhookEchoPayloadViewer';
import { 
  useWebhookEchoLogs, 
  useClearWebhookLogs, 
  useDeleteWebhookLog,
  type WebhookEchoLog 
} from '@/hooks/use-webhook-echo';
import { 
  Eye, 
  Trash2, 
  Copy, 
  RefreshCw, 
  AlertTriangle,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';

export default function WebhookEchoPage() {
  const { data: logs, isLoading, refetch } = useWebhookEchoLogs(50);
  const clearLogsMutation = useClearWebhookLogs();
  const deleteLogMutation = useDeleteWebhookLog();
  const { toast } = useToast();
  
  const [selectedLog, setSelectedLog] = useState<WebhookEchoLog | null>(null);
  const [showClearDialog, setShowClearDialog] = useState(false);

  // Construir URL do webhook
  const projectId = 'emyfvhrhwuhlmxewvcyk';
  const webhookUrl = `https://${projectId}.supabase.co/functions/v1/webhook-echo`;

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast({
      title: 'URL copiada!',
      description: 'A URL do webhook foi copiada para a área de transferência.',
    });
  };

  const handleClearLogs = () => {
    clearLogsMutation.mutate(undefined);
    setShowClearDialog(false);
  };

  const handleDeleteLog = (logId: string) => {
    deleteLogMutation.mutate(logId);
  };

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      GET: 'bg-blue-500',
      POST: 'bg-green-500',
      PUT: 'bg-yellow-500',
      PATCH: 'bg-orange-500',
      DELETE: 'bg-red-500',
    };
    return colors[method] || 'bg-gray-500';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Webhook Echo</h1>
        <p className="text-muted-foreground">
          Ferramenta de debug para receber e visualizar chamadas HTTP JSON
        </p>
      </div>

      {/* Webhook URL Card */}
      <Card>
        <CardHeader>
          <CardTitle>URL do Webhook</CardTitle>
          <CardDescription>
            Use esta URL para enviar requisições HTTP de teste
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={webhookUrl}
              readOnly
              className="font-mono text-sm"
            />
            <Button onClick={copyWebhookUrl} variant="outline">
              <Copy className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-2 text-sm text-muted-foreground">
            <p><strong>Exemplo de uso com cURL:</strong></p>
            <pre className="bg-muted p-3 rounded-md overflow-x-auto">
              {`curl -X POST ${webhookUrl} \\
  -H "Content-Type: application/json" \\
  -d '{"test": "data", "value": 123}'`}
            </pre>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Logs Recebidos</CardTitle>
            <CardDescription>
              Auto-refresh a cada 5 segundos • Últimos 50 registros
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setShowClearDialog(true)}
              disabled={!logs || logs.length === 0}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Limpar Todos
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="ml-2">Carregando logs...</p>
            </div>
          ) : !logs || logs.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                Nenhuma requisição recebida ainda
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Envie uma requisição HTTP para a URL acima para começar
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Método</TableHead>
                    <TableHead>Path</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <Badge className={getMethodColor(log.method)}>
                          {log.method}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {log.path || '/'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {log.ip_address || '-'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm:ss', {
                          locale: ptBR,
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedLog(log)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteLog(log.id)}
                            disabled={deleteLogMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payload Viewer Dialog */}
      <WebhookEchoPayloadViewer
        open={!!selectedLog}
        onOpenChange={(open) => !open && setSelectedLog(null)}
        log={selectedLog}
      />

      {/* Clear Logs Confirmation Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Limpar Todos os Logs?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Todos os logs do webhook echo serão
              permanentemente removidos do banco de dados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearLogs}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
